import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import PaymentReceiptPrint from './PaymentReceiptPrint';
import CancelPaymentModal from './CancelPaymentModal';
import { apiGet } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import './ViewPaymentDetail.css';

const ViewPaymentDetail = () => {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showPrintReceipt, setShowPrintReceipt] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  useEffect(() => {
    if (paymentId) {
      loadPaymentDetails();
    }
  }, [paymentId]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/payments/${paymentId}`);
      if (response && response.data) {
        setPayment(response.data);
        await loadInvoicesForPayment(response.data);
      }
    } catch (error) {
      console.error('Error loading payment details:', error);
      toastError('Failed to load payment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoicesForPayment = async (paymentData) => {
    try {
      if (paymentData.invoice_ids) {
        const invoiceIds = JSON.parse(paymentData.invoice_ids);
        const invoicePromises = invoiceIds.map(invoiceId => 
          apiGet(`/invoicing/invoices/${invoiceId}`)
        );
        
        const invoiceResponses = await Promise.all(invoicePromises);
        const invoiceData = invoiceResponses.map(response => response.data);
        setInvoices(invoiceData);
      }
    } catch (error) {
      console.error('Error loading invoices for payment:', error);
      toastError('Failed to load invoice details for payment.');
    }
  };

  const getInvoiceItemsColumns = () => [
    {
      Header: 'Type',
      accessor: 'invoice_item_type'
    },
    {
      Header: 'Description',
      accessor: 'description'
    },
    {
      Header: 'Quantity',
      accessor: 'quantity'
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ row }) => `₹${row.amount?.toFixed(2) || '0.00'}`
    }
  ];

  const getInvoiceItemsTableData = (invoice) => {
    if (!invoice || !invoice.invoice_items || !Array.isArray(invoice.invoice_items)) {
      return [];
    }
    return invoice.invoice_items;
  };

  const handlePrintReceipt = () => {
    setShowPrintReceipt(true);
  };

  const handleClosePrintReceipt = () => {
    setShowPrintReceipt(false);
  };

  const handleCancelPayment = () => {
    setShowCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handlePaymentCancelled = (updatedPayment) => {
    setPayment(updatedPayment);
    toastSuccess('Payment cancelled successfully');
  };

  const getInvoicesColumns = () => [
    {
      Header: 'Invoice #',
      accessor: 'invoice_number'
    },
    {
      Header: 'Date',
      accessor: 'invoice_date',
      Cell: ({ row }) => formatDateToDDMMYYYY(row.invoice_date)
    },
    {
      Header: 'Total Amount',
      accessor: 'total_amount',
      Cell: ({ row }) => `₹${row.total_amount?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Paid',
      accessor: 'amount_paid',
      Cell: ({ row }) => `₹${row.amount_paid?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Remaining',
      accessor: 'amount_remaining',
      Cell: ({ row }) => `₹${row.amount_remaining?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        const statusColors = {
          draft: 'bg-gray-100 text-gray-800',
          sent: 'bg-blue-100 text-blue-800',
          paid: 'bg-green-100 text-green-800',
          partial: 'bg-yellow-100 text-yellow-800',
          overdue: 'bg-red-100 text-red-800',
          cancelled: 'bg-red-100 text-red-800'
        };
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[row.status?.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
            {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || 'Draft'}
          </span>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" />
        <div className="rt-main-content">
          <div className="rt-main-section" style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RTSpinner fullScreen={true} />
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" />
        <div className="rt-main-content">
          <div className="rt-main-section">
            <div className="payment-detail-header">
              <RTButton 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="mb-4"
              >
                ← Back
              </RTButton>
              <h2>Payment not found</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" />
      <div className="rt-main-content">
        <div className="rt-main-section">
          <div className="payment-detail-header">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Payment Details</h2>
              <div className="flex gap-3">
                <RTButton 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                >
                  ← Back
                </RTButton>
                {payment.status?.toLowerCase() !== 'cancelled' && (
                  <>
                    <RTButton 
                      variant="primary"
                      onClick={handlePrintReceipt}
                    >
                      Print Receipt
                    </RTButton>
                    {payment.status?.toLowerCase() !== 'failed' && (
                      <RTButton 
                        variant="danger"
                        onClick={handleCancelPayment}
                      >
                        Cancel Payment
                      </RTButton>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Payment Information Card */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Payment ID</label>
                  <p className="text-sm font-mono">{payment.payment_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Payment Date</label>
                  <p className="text-sm">{formatDateToDDMMYYYY(payment.payment_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Amount</label>
                  <p className="text-lg font-semibold" style={{ color: '#153C7F' }}>₹{payment.amount?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Payment Method</label>
                  <p className="text-sm">{payment.payment_method?.charAt(0).toUpperCase() + payment.payment_method?.slice(1).toLowerCase() || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Reference Number</label>
                  <p className="text-sm">{payment.reference_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.status?.toLowerCase() === 'completed' ? 'bg-green-100 text-green-800' :
                      payment.status?.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      payment.status?.toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' :
                      payment.status?.toLowerCase() === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {payment.status?.charAt(0).toUpperCase() + payment.status?.slice(1) || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
              {payment.notes && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-600">Notes</label>
                  <p>{payment.notes}</p>
                </div>
              )}
              
              {/* Cancellation Information */}
              {payment.status?.toLowerCase() === 'cancelled' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-red-800 mb-2">Cancellation Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {payment.cancellation_date && (
                      <div>
                        <label className="text-sm font-medium text-red-700">Cancellation Date</label>
                        <p className="text-sm text-red-900">{formatDateToDDMMYYYY(payment.cancellation_date)}</p>
                      </div>
                    )}
                  </div>
                  {payment.cancellation_reason && (
                    <div className="mt-3">
                      <label className="text-sm font-medium text-red-700">Cancellation Reason</label>
                      <p className="text-sm text-red-900">{payment.cancellation_reason}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Invoices Paid Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Invoices Paid ({invoices.length})</h3>
              
              {invoices.length > 0 ? (
                <div className="space-y-6">
                  {invoices.map((invoice, index) => (
                    <div key={invoice.invoice_id} className="border-b pb-6 last:border-b-0 last:pb-0">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-semibold">Invoice #{invoice.invoice_number}</h4>
                          <p className="text-sm text-gray-600">Date: {formatDateToDDMMYYYY(invoice.invoice_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">Total: ₹{invoice.total_amount?.toFixed(2) || '0.00'}</p>
                          <p className="text-sm" style={{ color: '#153C7F' }}>Paid: ₹{invoice.amount_paid?.toFixed(2) || '0.00'}</p>
                          {invoice.amount_remaining > 0 && (
                            <p className="text-sm text-red-600">Remaining: ₹{invoice.amount_remaining?.toFixed(2) || '0.00'}</p>
                          )}
                        </div>
                      </div>

                      {/* Invoice Items Table */}
                      <div className="mt-4">
                        <h5 className="font-medium mb-2">Invoice Items</h5>
                        <Table
                          data={getInvoiceItemsTableData(invoice)}
                          columns={getInvoiceItemsColumns()}
                          showSearch={false}
                          exportable={false}
                          title=""
                          noDataText="No invoice items found"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No invoices found for this payment.</p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Receipt Print Component */}
        {showPrintReceipt && (
          <PaymentReceiptPrint
            paymentId={paymentId}
            onClose={handleClosePrintReceipt}
          />
        )}
        
        {/* Cancel Payment Modal */}
        <CancelPaymentModal
          isOpen={showCancelModal}
          onClose={handleCancelModalClose}
          payment={payment}
          onPaymentCancelled={handlePaymentCancelled}
        />
      </div>
    </div>
  );
};

export default ViewPaymentDetail;
